/*
  BerberNest rug purchase panel.
  - Standard sizes select an existing Shopify variant and drive the native product form.
  - Custom size: inline calculator (ft/cm, proportional diagram, area, price from the pricing rule).
    The browser never decides what the customer pays: custom sizes reach the cart only through the
    trusted endpoint configured on the block. Without one, the panel sends a request instead.
*/
(() => {
  const CM_PER_FT = 30.48;
  const SIZE_RE = /^\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*ft\s*=?\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/i;

  const round = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;
  const trim = (n, d) => String(round(n, d)).replace(/\.0+$/, '');

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [i, f] = num.toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (f ? decimal + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  function parseSize(value) {
    const m = SIZE_RE.exec(value || '');
    if (!m) return null;
    return { wFt: +m[1], lFt: +m[2], wCm: +m[3], lCm: +m[4] };
  }

  function parseNumber(raw) {
    const s = String(raw).trim().replace(',', '.');
    if (s === '') return { empty: true };
    if (!/^\d*\.?\d+$/.test(s) && !/^\d+\.$/.test(s)) return { invalid: true };
    return { value: parseFloat(s) };
  }

  function init(root) {
    if (root.dataset.bnReady) return;
    root.dataset.bnReady = 'true';

    const data = JSON.parse(root.querySelector('[data-bn-rug-data]').textContent);
    const id = root.dataset.blockId;
    const scope = root.closest('.product-details') || root.closest('product-component') || document;
    const form = scope.querySelector('form[data-type="add-to-cart-form"]');
    const idInput = form && form.querySelector('input[name="id"]');
    const atcButton = form && form.querySelector('button[name="add"]');
    const buyBlock = form && form.closest('.buy-buttons-block');

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    const sizeRadios = $$(`input[name="bn-size-${id}"]`);
    const fringeRadios = $$(`input[name="bn-fringe-${id}"]`);
    const select = $('[data-bn-size-select]');
    const priceEl = $('[data-bn-price]');
    const compareEl = $('[data-bn-compare]');
    const priceNoteEl = $('[data-bn-price-note]');
    const sizeValueEl = $('[data-bn-size-value]');
    const sizeDetailEl = $('[data-bn-size-detail]');
    const fringeValueEl = $('[data-bn-fringe-value]');
    const fringeFieldset = $('[data-bn-fringes]');
    const fringeHome = $('[data-bn-fringe-home]');
    const panel = $('[data-bn-custom]');
    const customRadio = $('[data-bn-custom-radio]');
    const defaultPriceNote = priceNoteEl ? priceNoteEl.textContent : '';

    const variants = data.variants;
    const sizesParsed = variants.map((v) => parseSize(v.size)).filter(Boolean);

    // Limits: the pricing rule wins; otherwise the range of this rug's listed sizes.
    const p = data.pricing || {};
    const shortSides = sizesParsed.map((s) => Math.min(s.wFt, s.lFt));
    const longSides = sizesParsed.map((s) => Math.max(s.wFt, s.lFt));
    const limits = {
      minSideFt: Number(p.minSideFt) || (shortSides.length ? Math.min(...shortSides) : 2),
      maxShortFt: Number(p.maxWidthFt) || (shortSides.length ? Math.max(...shortSides) : 20),
      maxLongFt: Number(p.maxLengthFt) || (longSides.length ? Math.max(...longSides) : 26),
    };

    const state = {
      mode: 'standard',
      size: null,
      lastStandardSize: null,
      fringe: null,
      unit: 'ft',
      dims: { width: null, length: null }, // canonical centimetres
      errors: { width: '', length: '' },
    };

    const initial = variants.find((v) => v.id === data.initialVariantId) || variants[0];
    state.size = initial.size;
    state.lastStandardSize = initial.size;
    state.fringe = initial.fringe;
    const seed = parseSize(initial.size) || sizesParsed[0];
    if (seed) state.dims = { width: seed.wFt * CM_PER_FT, length: seed.lFt * CM_PER_FT };

    const money = (cents) => formatMoney(cents, data.moneyFormat);
    const findVariant = (size, fringe) =>
      variants.find((v) => v.size === size && (data.fringeIndex < 0 || v.fringe === fringe));
    const fringeLabel = () => {
      const r = fringeRadios.find((x) => x.value === state.fringe);
      return r ? r.dataset.label : '';
    };

    /* ---------- custom size maths ---------- */
    const toUnit = (cm, unit) => (unit === 'ft' ? cm / CM_PER_FT : cm);
    const fromUnit = (v, unit) => (unit === 'ft' ? v * CM_PER_FT : v);
    const fmtDim = (cm, unit) => (unit === 'ft' ? trim(cm / CM_PER_FT, 2) : trim(cm, 1));
    // Custom sizes come in 0.5 ft steps (4, 4.5, 5 ...). STEP_CM is one step in centimetres.
    const STEP_CM = CM_PER_FT / 2;
    const snapCm = (cm) => Math.max(1, Math.round(cm / STEP_CM)) * STEP_CM;
    const onGrid = (cm) => Math.abs(cm - snapCm(cm)) <= 0.06; // tolerance for cm shown to 0.1

    function validate() {
      const errors = { width: '', length: '' };
      const { width, length } = state.dims;
      const minCm = limits.minSideFt * CM_PER_FT;
      ['width', 'length'].forEach((k) => {
        const v = state.dims[k];
        if (v === null) errors[k] = 'Enter a measurement, for example 8.5.';
        else if (Number.isNaN(v)) errors[k] = 'Use numbers only, for example 8.5.';
        else if (v < minCm - 0.01) errors[k] = `Minimum ${trim(limits.minSideFt, 2)} ft (${Math.ceil(minCm)} cm).`;
        else if (!onGrid(v)) {
          const lo = Math.floor(v / STEP_CM) * STEP_CM;
          const hi = lo + STEP_CM;
          errors[k] = state.unit === 'ft'
            ? `Sizes go in 0.5 ft steps: choose ${fmtDim(lo, 'ft')} or ${fmtDim(hi, 'ft')}.`
            : `Sizes go in 0.5 ft steps: choose ${fmtDim(lo, 'cm')} cm (${fmtDim(lo, 'ft')} ft) or ${fmtDim(hi, 'cm')} cm (${fmtDim(hi, 'ft')} ft).`;
        }
      });
      if (!errors.width && !errors.length) {
        const shortCm = Math.min(width, length);
        const longCm = Math.max(width, length);
        if (shortCm > limits.maxShortFt * CM_PER_FT + 0.01 || longCm > limits.maxLongFt * CM_PER_FT + 0.01) {
          const msg = `Up to ${trim(limits.maxShortFt, 2)} × ${trim(limits.maxLongFt, 2)} ft (${Math.floor(limits.maxShortFt * CM_PER_FT)} × ${Math.floor(limits.maxLongFt * CM_PER_FT)} cm).`;
          // Flag the side that breaks the limit: the longer side if it is too long, otherwise the shorter one.
          const longKey = width > length ? 'width' : 'length';
          const shortKey = longKey === 'width' ? 'length' : 'width';
          errors[longCm > limits.maxLongFt * CM_PER_FT + 0.01 ? longKey : shortKey] = msg;
        }
      }
      state.errors = errors;
      return !errors.width && !errors.length;
    }

    function customQuote() {
      if (!validate()) return null;
      const wFt = state.dims.width / CM_PER_FT;
      const lFt = state.dims.length / CM_PER_FT;
      const areaFt = wFt * lFt;
      const areaM2 = (state.dims.width / 100) * (state.dims.length / 100);
      let cents = null;
      if (data.pricing && Number(data.pricing.ratePerSqM) > 0) {
        // Pricing policy: the customer's exact dimensions (never rounded or enlarged) in square
        // metres x the rate per m², shown to the cent. The fringe choice does not change the price.
        const amount = Math.max(areaM2 * Number(data.pricing.ratePerSqM), Number(data.pricing.minimumCharge) || 0);
        const fx = (window.Shopify && Shopify.currency && Number(Shopify.currency.rate)) || 1;
        cents = Math.round(amount * fx * 100);
      }
      return { wFt, lFt, areaFt, areaM2, cents };
    }

    // The rate and area are not shown to customers; only the size and its price.
    function rateNote() {
      return data.labels.customPriceNote;
    }

    /* ---------- diagram ---------- */
    const diagram = $('[data-bn-diagram]');
    const diagramText = $('[data-bn-diagram-text]');
    function drawDiagram(q) {
      if (!diagram) return;
      const W = 320, H = 170, maxW = 200, maxH = 112;
      const wCm = state.dims.width, lCm = state.dims.length;
      if (!(wCm > 0) || !(lCm > 0)) { diagram.innerHTML = ''; return; }
      // One scale for every size (the largest allowed rug fills the box), so bigger rugs read
      // bigger; very small rugs are enlarged just enough to stay legible.
      const fixed = Math.min(maxW / (limits.maxShortFt * CM_PER_FT), maxH / (limits.maxLongFt * CM_PER_FT));
      const fit = Math.min(maxW / wCm, maxH / lCm);
      const s = Math.min(fit, Math.max(fixed, 26 / Math.min(wCm, lCm)));
      const rw = Math.max(wCm * s, 6), rh = Math.max(lCm * s, 6);
      const x = (W - rw) / 2 - 12, y = (H - rh) / 2 - 8;
      const fringe = (state.fringe || '').toLowerCase();
      const fringeTop = /2|both|two/.test(fringe);
      const fringeBottom = fringeTop || /1|one/.test(fringe);
      const tick = (yy, dir) => {
        let out = '';
        const n = Math.max(4, Math.round(rw / 5));
        for (let i = 0; i <= n; i++) {
          const xx = x + (rw * i) / n;
          out += `<line x1="${xx.toFixed(1)}" y1="${yy}" x2="${xx.toFixed(1)}" y2="${(yy + dir * 5).toFixed(1)}" />`;
        }
        return out;
      };
      const u = state.unit;
      const wl = `${fmtDim(wCm, u)} ${u}`, ll = `${fmtDim(lCm, u)} ${u}`;
      let grid = '';
      for (let gx = 20; gx < W; gx += 20) grid += `<line x1="${gx}" y1="0" x2="${gx}" y2="${H}" />`;
      diagram.innerHTML = `
        <svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
          <g stroke="rgba(46,38,32,0.06)" stroke-width="1">${grid}</g>
          <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${rw.toFixed(1)}" height="${rh.toFixed(1)}" fill="#E2D6BF" stroke="#2E2620" stroke-width="1" />
          <g stroke="#2E2620" stroke-width="0.8" opacity="0.7">
            ${fringeTop ? tick(y, -1) : ''}
            ${fringeBottom ? tick(y + rh, 1) : ''}
          </g>
          <g stroke="#2E2620" stroke-width="0.8" fill="none">
            <line x1="${x.toFixed(1)}" y1="${(y + rh + 16).toFixed(1)}" x2="${(x + rw).toFixed(1)}" y2="${(y + rh + 16).toFixed(1)}" />
            <line x1="${x.toFixed(1)}" y1="${(y + rh + 12).toFixed(1)}" x2="${x.toFixed(1)}" y2="${(y + rh + 20).toFixed(1)}" />
            <line x1="${(x + rw).toFixed(1)}" y1="${(y + rh + 12).toFixed(1)}" x2="${(x + rw).toFixed(1)}" y2="${(y + rh + 20).toFixed(1)}" />
            <line x1="${(x + rw + 16).toFixed(1)}" y1="${y.toFixed(1)}" x2="${(x + rw + 16).toFixed(1)}" y2="${(y + rh).toFixed(1)}" />
            <line x1="${(x + rw + 12).toFixed(1)}" y1="${y.toFixed(1)}" x2="${(x + rw + 20).toFixed(1)}" y2="${y.toFixed(1)}" />
            <line x1="${(x + rw + 12).toFixed(1)}" y1="${(y + rh).toFixed(1)}" x2="${(x + rw + 20).toFixed(1)}" y2="${(y + rh).toFixed(1)}" />
          </g>
          <g font-family="inherit" font-size="11" fill="#2E2620">
            <text x="${(x + rw / 2).toFixed(1)}" y="${(y + rh + 32).toFixed(1)}" text-anchor="middle">${wl}</text>
            <text x="${(x + rw + 24).toFixed(1)}" y="${(y + rh / 2 + 4).toFixed(1)}">${ll}</text>
          </g>
        </svg>`;
      if (diagramText) diagramText.textContent = q ? `Diagram: ${wl} wide by ${ll} long.` : '';
    }

    /* ---------- rendering ---------- */
    const unitLabels = $$('[data-bn-unit-label]');
    const inputs = { width: $('[data-bn-dim="width"]'), length: $('[data-bn-dim="length"]') };
    const errorEls = { width: $('[data-bn-error="width"]'), length: $('[data-bn-error="length"]') };
    const limitsEl = $('[data-bn-limits]');
    const customPriceEl = $('[data-bn-custom-price]');
    const customMetaEl = $('[data-bn-custom-meta]');
    const areaEl = $('[data-bn-area]');
    const requestBox = $('[data-bn-custom-request]');
    const requestSubmit = $('[data-bn-request-submit]');
    const sticky = $('[data-bn-sticky]');
    const stickySize = $('[data-bn-sticky-size]');
    const stickyPrice = $('[data-bn-sticky-price]');
    const stickyButton = $('[data-bn-sticky-button]');
    const checkoutMode = data.endpoint ? 'endpoint' : 'request';
    root.dataset.checkout = checkoutMode;

    function setAtcLabel(text) {
      if (!atcButton) return;
      const node = Array.from(atcButton.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
      if (node) node.textContent = text;
      else atcButton.insertBefore(document.createTextNode(text), atcButton.querySelector('.add-to-cart__added'));
    }

    function writeInputs() {
      ['width', 'length'].forEach((k) => {
        const el = inputs[k];
        if (!el || document.activeElement === el) return;
        const v = state.dims[k];
        el.value = v === null || Number.isNaN(v) ? el.value : fmtDim(v, state.unit);
      });
    }

    function render() {
      const custom = state.mode === 'custom';
      root.dataset.mode = state.mode;

      // Size controls: exactly one active selection.
      sizeRadios.forEach((r) => {
        r.checked = custom ? r === customRadio : r.value === state.size;
      });
      if (customRadio) customRadio.setAttribute('aria-expanded', String(custom));
      if (select) select.value = custom ? '' : state.size || '';
      fringeRadios.forEach((r) => { r.checked = r.value === state.fringe; });
      if (fringeValueEl) fringeValueEl.textContent = fringeLabel();

      if (panel) {
        panel.hidden = !custom;
        const slot = panel.querySelector('[data-bn-fringe-slot]');
        if (fringeFieldset) {
          if (custom && slot && fringeFieldset.parentElement !== slot) slot.appendChild(fringeFieldset);
          if (!custom && fringeHome && fringeFieldset.parentElement !== fringeHome) fringeHome.appendChild(fringeFieldset);
        }
      }

      if (!custom) {
        const v = findVariant(state.size, state.fringe);
        const parsed = parseSize(state.size);
        if (sizeValueEl) sizeValueEl.textContent = parsed ? `${trim(parsed.wFt, 2)}′ × ${trim(parsed.lFt, 2)}′` : state.size || '';
        if (sizeDetailEl) sizeDetailEl.textContent = '';
        if (priceNoteEl) priceNoteEl.textContent = defaultPriceNote;
        if (v) {
          priceEl.textContent = money(v.price);
          if (compareEl) {
            compareEl.hidden = !(v.compare > v.price);
            compareEl.textContent = v.compare > v.price ? money(v.compare) : '';
          }
          if (idInput) idInput.value = v.id;
          if (atcButton) {
            atcButton.disabled = !v.available;
            setAtcLabel(v.available ? `${data.labels.addToCart} · ${money(v.price)}` : data.labels.soldOut);
          }
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          url.searchParams.delete('contact_posted');
          window.history.replaceState(window.history.state, '', url.toString());
        } else {
          priceEl.textContent = '—';
          if (atcButton) { atcButton.disabled = true; setAtcLabel(data.labels.unavailable); }
        }
        if (sticky) {
          stickySize.textContent = [sizeValueEl ? sizeValueEl.textContent : '', fringeLabel()].filter(Boolean).join(' · ');
          stickyPrice.textContent = v ? money(v.price) : '';
          stickyButton.textContent = v && v.available ? data.labels.addToCart : data.labels.soldOut;
          stickyButton.disabled = !(v && v.available);
        }
        return;
      }

      // Custom mode
      unitLabels.forEach((el) => { el.textContent = state.unit; });
      writeInputs();
      const q = customQuote();
      ['width', 'length'].forEach((k) => {
        if (errorEls[k]) errorEls[k].textContent = state.errors[k];
        const stepper = inputs[k] && inputs[k].closest('.bn-custom__stepper');
        if (stepper) stepper.toggleAttribute('data-invalid', !!state.errors[k]);
        if (inputs[k]) inputs[k].setAttribute('aria-invalid', String(!!state.errors[k]));
      });
      if (limitsEl) {
        const u = state.unit;
        const min = u === 'ft' ? trim(limits.minSideFt, 2) : Math.ceil(limits.minSideFt * CM_PER_FT);
        const maxS = u === 'ft' ? trim(limits.maxShortFt, 2) : Math.floor(limits.maxShortFt * CM_PER_FT);
        const maxL = u === 'ft' ? trim(limits.maxLongFt, 2) : Math.floor(limits.maxLongFt * CM_PER_FT);
        limitsEl.textContent = u === 'ft'
          ? `From ${min} to ${maxS} × ${maxL} ft · in 0.5 ft steps`
          : `From ${min} to ${maxS} × ${maxL} cm · in 0.5 ft (15.24 cm) steps`;
      }
      drawDiagram(q);
      const sizeText = q ? `${fmtDim(state.dims.width, 'ft')} × ${fmtDim(state.dims.length, 'ft')} ft` : '';
      const sizeCm = q ? `${fmtDim(state.dims.width, 'cm')} × ${fmtDim(state.dims.length, 'cm')} cm` : '';
      if (sizeValueEl) sizeValueEl.textContent = q ? (state.unit === 'ft' ? sizeText : sizeCm) : data.labels.customSize;
      if (areaEl) areaEl.textContent = '';
      const priceText = q ? (q.cents !== null ? money(q.cents) : data.labels.priceOnRequest) : '—';
      if (customPriceEl) customPriceEl.textContent = priceText;
      if (customMetaEl) customMetaEl.textContent = q ? rateNote() : '';
      priceEl.textContent = priceText;
      if (compareEl) compareEl.hidden = true;
      if (priceNoteEl) priceNoteEl.textContent = data.labels.customPriceNote;
      if (sizeDetailEl) sizeDetailEl.textContent = q ? (state.unit === 'ft' ? sizeCm : sizeText) : '';
      if (requestSubmit) requestSubmit.disabled = !q;

      // Request form payload (normalised dimensions in both units).
      const setField = (k, v) => { const el = root.querySelector(`[data-bn-form="${k}"]`); if (el) el.value = v; };
      setField('size', sizeText);
      setField('size_cm', sizeCm);
      setField('unit', state.unit);
      setField('fringe', fringeLabel());
      setField('area', q ? `${trim(q.areaFt, 2)} sq ft (${trim(q.areaM2, 2)} m²)` : '');
      setField('price', q && q.cents !== null ? money(q.cents) : 'Not configured');

      // A standard variant must never be bought while a custom size is shown.
      if (checkoutMode === 'request' && atcButton) atcButton.disabled = true;
      if (checkoutMode === 'endpoint' && atcButton) {
        atcButton.disabled = !q || q.cents === null;
        setAtcLabel(q && q.cents !== null ? `${data.labels.customCheckout} · ${money(q.cents)}` : data.labels.customCheckout);
      }
      if (sticky) {
        stickySize.textContent = q ? `${state.unit === 'ft' ? sizeText : sizeCm} · ${fringeLabel()}` : data.labels.customSize;
        stickyPrice.textContent = q ? priceText : '';
        stickyButton.textContent = checkoutMode === 'endpoint' ? data.labels.customCheckout : data.labels.requestSize;
        stickyButton.disabled = !q;
      }
    }

    /* ---------- events ---------- */
    function enterCustom() {
      if (state.mode !== 'custom') state.lastStandardSize = state.size;
      state.mode = 'custom';
      render();
    }
    function exitCustom(focusBack) {
      state.mode = 'standard';
      state.size = state.lastStandardSize || state.size;
      render();
      if (focusBack && customRadio) customRadio.focus();
    }

    sizeRadios.forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      if (r === customRadio) {
        enterCustom();
        const first = inputs.width;
        if (first && !matchMedia('(pointer: coarse)').matches) first.focus({ preventScroll: true });
        return;
      }
      state.mode = 'standard';
      state.size = r.value;
      state.lastStandardSize = r.value;
      render();
    }));
    if (customRadio) {
      // Clicking the already-selected custom option should not leave the panel in a strange state.
      customRadio.addEventListener('click', () => { if (state.mode === 'custom' && panel && panel.hidden) render(); });
    }
    if (select) select.addEventListener('change', () => {
      if (!select.value) { render(); return; }
      state.mode = 'standard';
      state.size = select.value;
      state.lastStandardSize = select.value;
      render();
    });
    fringeRadios.forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      state.fringe = r.value; // custom dimensions are kept as they are
      render();
    }));

    const closeBtn = $('[data-bn-custom-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => exitCustom(true));
    if (panel) panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); exitCustom(true); }
    });

    $$('[data-bn-unit]').forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      // Commit any half-typed values first, then show the same physical size in the new unit.
      ['width', 'length'].forEach((k) => commit(k, true));
      state.unit = r.value;
      render();
    }));

    function commit(k, snap) {
      const el = inputs[k];
      if (!el) return;
      const parsed = parseNumber(el.value);
      if (parsed.empty) state.dims[k] = null;
      else if (parsed.invalid) state.dims[k] = NaN;
      else {
        const cm = fromUnit(parsed.value, state.unit);
        // On a 0.5 ft step (cm values to 0.1 cm included): use the exact step. When the field is
        // left, any other value is rounded to the nearest step and shown in the field.
        state.dims[k] = onGrid(cm) || snap ? snapCm(cm) : cm;
        if (snap && !onGrid(cm)) el.value = fmtDim(state.dims[k], state.unit);
      }
    }
    ['width', 'length'].forEach((k) => {
      const el = inputs[k];
      if (!el) return;
      el.addEventListener('input', () => { commit(k); render(); });
      el.addEventListener('blur', () => { commit(k, true); render(); });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          step(k, e.key === 'ArrowUp' ? 1 : -1);
        }
      });
    });

    function step(k, dir) {
      // One step is 0.5 ft (15.24 cm) in either unit, always landing on the 0.5 ft grid.
      const current = state.dims[k];
      const base = current === null || Number.isNaN(current) ? limits.minSideFt * CM_PER_FT : current;
      // From a value between steps, the first press goes to the neighbouring step (4.76 -> 5 or 4.5).
      let next = onGrid(base)
        ? snapCm(base) + dir * STEP_CM
        : (dir > 0 ? Math.ceil(base / STEP_CM) : Math.floor(base / STEP_CM)) * STEP_CM;
      next = Math.max(next, snapCm(limits.minSideFt * CM_PER_FT));
      state.dims[k] = next;
      if (inputs[k]) inputs[k].value = fmtDim(state.dims[k], state.unit);
      render();
    }
    $$('[data-bn-step]').forEach((b) => b.addEventListener('click', () => step(b.dataset.bnTarget, Number(b.dataset.bnStep))));

    /* Trusted checkout: only used when a pricing endpoint is configured.
       The endpoint re-validates and re-prices the size on the server, then returns either a
       checkout_url (draft-order checkout at the server price) or a server-priced variant_id. */
    let bypass = false;
    let busy = false;
    const errorEl = $('[data-bn-checkout-error]');
    function showCheckoutError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.hidden = !msg;
    }
    const ERROR_TEXT = {
      too_small: 'This size is below our minimum. Please check the measurements.',
      too_large: 'This size is above our maximum. Please contact us for larger rugs.',
      not_available: 'Custom sizes are not available for this rug.',
      invalid_step: 'Sizes go in 0.5 ft steps. Please adjust the measurements.',
    };
    async function addCustomToCart() {
      const q = customQuote();
      if (!q || !form || busy) return;
      busy = true;
      showCheckoutError('');
      if (atcButton) atcButton.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(data.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            product_id: data.productId,
            width_cm: state.dims.width,
            length_cm: state.dims.length,
            unit: state.unit,
            fringe: state.fringe,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(ERROR_TEXT[json.error] || 'We could not open checkout for this size. Please try again.');
        if (json.checkout_url) {
          window.location.assign(json.checkout_url);
          return;
        }
        if (!json.variant_id) throw new Error('We could not open checkout for this size. Please try again.');
        form.querySelectorAll('[data-bn-prop]').forEach((el) => el.remove());
        const props = Object.assign({
          'Custom size': `${fmtDim(state.dims.width, 'ft')} × ${fmtDim(state.dims.length, 'ft')} ft`,
          'Custom size (cm)': `${fmtDim(state.dims.width, 'cm')} × ${fmtDim(state.dims.length, 'cm')} cm`,
          Fringes: fringeLabel(),
        }, json.properties || {});
        Object.entries(props).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          input.dataset.bnProp = '';
          form.appendChild(input);
        });
        idInput.value = json.variant_id;
        bypass = true;
        form.requestSubmit(atcButton);
        bypass = false;
      } finally {
        busy = false;
        if (atcButton) atcButton.removeAttribute('aria-busy');
      }
    }
    if (form && checkoutMode === 'endpoint') {
      form.addEventListener('submit', (e) => {
        if (state.mode !== 'custom' || bypass) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        addCustomToCart().catch((err) => showCheckoutError(err.message));
      }, true);
    }
    // Standard sizes never carry custom-size properties.
    if (form) form.addEventListener('submit', () => {
      if (state.mode !== 'custom') form.querySelectorAll('[data-bn-prop]').forEach((el) => el.remove());
    }, true);

    /* ---------- size guide ---------- */
    const guide = $('[data-bn-guide]');
    const guideOpen = $('[data-bn-guide-open]');
    if (guide && guideOpen) {
      guideOpen.addEventListener('click', () => guide.showModal());
      $('[data-bn-guide-close]').addEventListener('click', () => guide.close());
      guide.addEventListener('click', (e) => { if (e.target === guide) guide.close(); });
    }

    /* ---------- request sent ---------- */
    if (new URL(window.location.href).searchParams.get('contact_posted') === 'true') {
      const sent = $('[data-bn-sent]');
      if (sent) sent.hidden = false;
    }

    /* ---------- mobile sticky bar ---------- */
    if (sticky) {
      sticky.hidden = false;
      let ticking = false;
      const update = () => {
        ticking = false;
        const ref = state.mode === 'custom' && checkoutMode === 'request' ? requestBox : buyBlock || root;
        if (!ref) return;
        const r = ref.getBoundingClientRect();
        const inView = r.bottom > 0 && r.top < window.innerHeight;
        sticky.toggleAttribute('data-visible', !inView && r.height > 0);
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      root.addEventListener('change', onScroll);
      setTimeout(update, 50);
      stickyButton.addEventListener('click', () => {
        if (state.mode === 'custom' && checkoutMode === 'request') {
          requestBox.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
          const email = requestBox.querySelector('input[type="email"]');
          if (email) setTimeout(() => email.focus({ preventScroll: true }), 400);
          return;
        }
        if (atcButton && !atcButton.disabled) atcButton.click();
      });
    }

    render();
  }

  const boot = () => document.querySelectorAll('[data-bn-rug]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
